import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, LogOut, Bike, Store, RefreshCw, Inbox } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('ifd_admin_token', res.data.token);
      onLogin(res.data.token);
    } catch (err) {
      toast({ title: 'Accesso negato', description: 'Password errata.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/40 px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-border bg-white p-8 shadow-sm">
        <div className="flex flex-col items-center text-center mb-6">
          <img src="/logo-ifd.jpg" alt="IFD" className="h-14 w-14 rounded-xl object-cover object-top mb-3" />
          <h1 className="font-display text-2xl font-extrabold text-foreground">Area Admin</h1>
          <p className="text-sm text-muted-foreground mt-1">Italian Fast Delivery</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pwd">Password</Label>
          <Input id="pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoFocus />
        </div>
        <Button type="submit" disabled={loading} className="w-full mt-5 gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
          <Lock className="h-4 w-4" /> {loading ? 'Accesso...' : 'Accedi'}
        </Button>
      </form>
    </div>
  );
};

const ApplicationCard = ({ app }) => {
  const isRider = app.type === 'rider';
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {isRider ? <Bike className="h-5 w-5 text-primary" /> : <Store className="h-5 w-5 text-primary" />}
          <h3 className="font-display font-bold text-foreground">{isRider ? app.name : app.restaurant}</h3>
        </div>
        <Badge variant="secondary" className="shrink-0 text-xs">{formatDate(app.created_at)}</Badge>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        {!isRider && <p><span className="text-muted-foreground">Referente: </span>{app.contact}</p>}
        <p><span className="text-muted-foreground">Email: </span><a href={`mailto:${app.email}`} className="text-primary">{app.email}</a></p>
        <p><span className="text-muted-foreground">Telefono: </span>{app.phone}</p>
        <p><span className="text-muted-foreground">Città: </span>{app.city}</p>
        {isRider && <p><span className="text-muted-foreground">Mezzo: </span>{app.vehicle}</p>}
        {!isRider && app.cuisine && <p><span className="text-muted-foreground">Cucina: </span>{app.cuisine}</p>}
      </div>
      {app.note && <p className="mt-3 text-sm text-muted-foreground bg-secondary/60 rounded-lg px-3 py-2">“{app.note}”</p>}
    </div>
  );
};

const EmptyState = ({ label }) => (
  <div className="text-center py-16 text-muted-foreground">
    <Inbox className="h-10 w-10 mx-auto mb-3 opacity-50" />
    <p>Nessuna {label} ricevuta al momento.</p>
  </div>
);

const Dashboard = ({ token, onLogout }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/applications`, { headers: { Authorization: `Bearer ${token}` } });
      setApps(res.data.applications || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast({ title: 'Sessione scaduta', description: 'Effettua di nuovo l\'accesso.', variant: 'destructive' });
        onLogout();
      } else {
        toast({ title: 'Errore', description: 'Impossibile caricare i dati.', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout, toast]);

  useEffect(() => { load(); }, [load]);

  const riders = apps.filter((a) => a.type === 'rider');
  const partners = apps.filter((a) => a.type === 'partner');

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-ifd.jpg" alt="IFD" className="h-10 w-10 rounded-lg object-cover object-top" />
            <div>
              <p className="font-display font-extrabold text-foreground leading-none">Area Admin</p>
              <p className="text-xs text-muted-foreground">Candidature ricevute</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" /> Aggiorna</Button>
            <Button variant="ghost" size="sm" onClick={onLogout} className="gap-2 text-muted-foreground"><LogOut className="h-4 w-4" /> Esci</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-8">
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="rounded-xl border border-border bg-white p-5">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Bike className="h-4 w-4 text-primary" /> Rider</div>
            <p className="font-display text-3xl font-extrabold text-foreground mt-1">{riders.length}</p>
          </div>
          <div className="rounded-xl border border-border bg-white p-5">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Store className="h-4 w-4 text-primary" /> Partner</div>
            <p className="font-display text-3xl font-extrabold text-foreground mt-1">{partners.length}</p>
          </div>
        </div>

        <Tabs defaultValue="rider">
          <TabsList className="mb-5">
            <TabsTrigger value="rider" className="gap-2"><Bike className="h-4 w-4" /> Rider ({riders.length})</TabsTrigger>
            <TabsTrigger value="partner" className="gap-2"><Store className="h-4 w-4" /> Partner ({partners.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="rider">
            {loading ? <p className="text-muted-foreground py-10 text-center">Caricamento...</p> : riders.length === 0 ? <EmptyState label="candidatura rider" /> : (
              <div className="space-y-4">{riders.map((a) => <ApplicationCard key={a.id} app={a} />)}</div>
            )}
          </TabsContent>
          <TabsContent value="partner">
            {loading ? <p className="text-muted-foreground py-10 text-center">Caricamento...</p> : partners.length === 0 ? <EmptyState label="richiesta partner" /> : (
              <div className="space-y-4">{partners.map((a) => <ApplicationCard key={a.id} app={a} />)}</div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem('ifd_admin_token'));

  const handleLogout = () => {
    localStorage.removeItem('ifd_admin_token');
    setToken(null);
  };

  if (!token) return <LoginScreen onLogin={setToken} />;
  return <Dashboard token={token} onLogout={handleLogout} />;
};

export default Admin;
